import type { BookingFor } from "./enums.ts";

/**
 * The welcome before a person's first class at a business (C3). A worker job
 * (`jobs/first-class-welcome.ts`) sends it once for each participant and business, the chosen hours
 * before the class, by push and WhatsApp. The owner turns it on or off, picks the hours and writes
 * the text on Desk (Settings, Messages).
 *
 * The start value follows who books (`booking_for`), never the sport: on where clients book for
 * themselves (a new adult wants to know what to bring), off for a family business, where the owner
 * already speaks to each new family.
 */

/** The badge on a class list next to a person whose first class it is (Desk and the phone). */
export const FIRST_CLASS_LABEL = "First class";

/** The text a business sends until the owner writes its own. ASD-STE100: short, one instruction each. */
export const FIRST_CLASS_WELCOME_TEXT =
  "Welcome to your first class. Wear comfortable sports clothes and bring a bottle of water. Please come 10 minutes before the start. Then we have time to meet you before the class.";

/** The hours before the class the owner can pick. */
export const FIRST_CLASS_WELCOME_HOURS = [2, 4, 12, 24, 48] as const;

export const FIRST_CLASS_WELCOME_MIN_TEXT = 10;
export const FIRST_CLASS_WELCOME_MAX_TEXT = 500;

export type FirstClassWelcome = {
  on: boolean;
  /** One of `FIRST_CLASS_WELCOME_HOURS`. */
  hoursBefore: number;
  text: string;
};

/** The welcome of a business that did not change it: on for `self`, off for `children`, 24 hours, the default text. */
export function defaultFirstClassWelcome(bookingFor: BookingFor): FirstClassWelcome {
  return { on: bookingFor === "self", hoursBefore: 24, text: FIRST_CLASS_WELCOME_TEXT };
}

/** The stored welcome with every missing part filled from the start values. */
export function firstClassWelcomeOf(stored: Partial<FirstClassWelcome> | null | undefined, bookingFor: BookingFor): FirstClassWelcome {
  const start = defaultFirstClassWelcome(bookingFor);
  const text = typeof stored?.text === "string" && stored.text.trim() !== "" ? stored.text : start.text;
  return {
    on: typeof stored?.on === "boolean" ? stored.on : start.on,
    hoursBefore: typeof stored?.hoursBefore === "number" && stored.hoursBefore > 0 ? stored.hoursBefore : start.hoursBefore,
    text,
  };
}

/**
 * True when the welcome for a class at `startsAt` goes out now: the class starts in `hoursBefore`
 * hours or less and has not started. Absolute time, so the business clock does not change it.
 */
export function firstClassWelcomeDue(startsAt: Date, now: Date, hoursBefore: number): boolean {
  const start = startsAt.getTime();
  return now.getTime() < start && start - now.getTime() <= hoursBefore * 3_600_000;
}

/**
 * The owner's text as one WhatsApp template variable: Meta refuses a new line, a tab and four spaces
 * in a row in a variable, so they become single spaces.
 */
export function welcomeTextForTemplate(text: string): string {
  return text.replace(/\s+/g, " ").trim();
}
